import React from "react";

import styled from "@emotion/styled";

// hightchart
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import Typography from "../mui/Typography";
import Stack from "../mui/Stack";
import Box from "../mui/Box";
import MagicLoadingBox from "../common/MagicLoadingBox";

// interface
interface Quarters {
  quarter: string;
  color: string;
}

interface SeriesData {
  name: string;
  data: number[];
  color: string;
}

interface Props {
  chartData:
    | {
        seriesData: SeriesData[];
        categories: string[];
      }
    | {};

  quarters: Quarters[];
  isLoading: boolean;
}

const IndividualIndicatorsGraph: React.FC<Props> = (props) => {
  const { chartData, quarters, isLoading } = props;

  let categories: string[] = [];
  let seriesData: SeriesData[] = [];

  if ("seriesData" in chartData && "categories" in chartData) {
    ({ categories, seriesData } = chartData);
  }

  // handle Loading state of api data
  if (isLoading) {
    return <MagicLoadingBox height={500} />;
  }

  const highestNumberIdentifier = Math.max(
    ...seriesData.flatMap((item: any) => item.data.map((data: any) => data.y))
  );

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "white",
      marginBottom: 80,
      scrollablePlotArea: {
        minWidth: 2500, // Changed from 4000 to 0
        scrollPositionX: 0,
      },
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: categories,
      crosshair: true, // Highlights x-axis on hover
      labels: {
        useHTML: true,
        style:{
          cursor:"pointer"
        },
        formatter: function () {
          const words = this.value.toString().split(" ");
          const truncated =
            words.length > 3
              ? words.slice(0, 3).join(" ") + "..."
              : this.value;
          return `<span title="${this.value}">${truncated}</span>`; // Tooltip on hover
        },
      },
    },
    yAxis: {
      min: 0,
      max:
        highestNumberIdentifier > 500
          ? highestNumberIdentifier + 50
          : highestNumberIdentifier + 100,
      title: { text: "" },
    },
    tooltip: {
      enabled: true,
      useHTML: true,
      style: {
        fontSize: "14px",
        color: "gray",
        fontWeight: "500",
        textAlign: "center",
        zIndex: "1 !important",
      },
      positioner: function (labelWidth, labelHeight, point) {
        let tooltipX = point.plotX + this.chart.plotLeft - labelWidth / 2;
        let tooltipY = point.plotY + this.chart.plotTop - labelHeight - 30; // Add 20px margin from the bottom
        return { x: tooltipX, y: tooltipY };
      },
      shadow: true,
      borderWidth: 1,
      borderColor: "#ccc",
    },
    exporting: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          useHTML: true,
          style: {
            fontSize: "10px",
            color: "#344054",
            fontWeight: "500",
            textAlign: "center",
          },
          formatter: function () {
            const percentage = this?.point?.percentage ?? 0;
            const count = this?.point?.y ?? 0;
            const totalCenters = this?.point?.total ?? 0;
            return `
              <div style="
                display: inline-block;
                position: relative;
                background-color: #ffffff;
                color: #344054;
                padding: 5px;
                border-radius: 8px;
                font-weight: 500;
              ">
                ${totalCenters} <br/>
                (${percentage.toFixed(0)}%) <br/>
                <div style="
                  content: '';
                  position: absolute;
                  bottom: -8px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 0;
                  height: 0;
                  border-left: 8px solid transparent;
                  border-right: 8px solid transparent;
                  border-top: 8px solid #ffffff;
                "></div>
              </div>`;
          },
        },
      },
    },

    series: seriesData ? JSON.parse(JSON.stringify(seriesData)) : [],
  };

  return (
    <StyledStackContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="15px"
      >
        <StyledGraphHeading>
          Individual Indicators in Performance
        </StyledGraphHeading>

        {/* Legend  */}
        <Stack direction="row" gap="16px">
          {quarters?.map((info: Quarters, index: number) => (
            <Stack direction="row" gap="8px" key={index}>
              <StyledLegendBox style={{ backgroundColor: info.color }} />
              <StyledLegendTxt> {info.quarter}</StyledLegendTxt>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Box sx={{ height: "2px", backgroundColor: "#D7D7D7", mb: "20px" }} />

      {seriesData?.[0]?.data.length !== 0 &&
      seriesData?.[1]?.data.length !== 0 ? (
        <div style={{ padding: "20px" }}>
          <HighchartsReact highcharts={Highcharts} options={options} />
        </div>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "300px",
          }}
        >
          <Typography>No data found</Typography>
        </Box>
      )}
    </StyledStackContainer>
  );
};

const StyledStackContainer = styled(Stack)({
  width: "100%",
  margin: "0 auto",
  border: "1px solid #D7D7D7",
  marginBottom: "24px",
  borderRadius: "16px",
  padding: "20px",
  boxSizing: "border-box",
  overflow: "hidden",
  backgroundColor: "white",
  
  // Add scrollbar styling
  "& ::-webkit-scrollbar": {
    height: "8px",
  },
  "& ::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "4px",
  },
  "& ::-webkit-scrollbar-thumb": {
    background: "#959595",
    borderRadius: "4px",
    "&:hover": {
      background: "#555",
    },
  },
});

const StyledGraphHeading = styled(Typography)({
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: "16.94px",
});

const StyledLegendBox = styled(Box)({
  borderRadius: "4px",
  width: "20px",
  height: "20px",
  cursor: "pointer",
});

const StyledLegendTxt = styled(Typography)({
  margin: 0,
  fontSize: "14px",
  color: "#333",
  fontWeight: "500",
  lineHeight: "20px",
});

export default IndividualIndicatorsGraph;
